<script setup lang="ts">
import { computed, ref, watch } from "vue";
import DRBtn from "../DRBtn.vue";
import type { RulesProps } from "@/composable/useValidation";
import { useScreen } from "@/composable/useResponsive";

const props = defineProps<{
  textarea?: boolean;
  modelValue: boolean;
  title: string;
  label?: string;
  value?: string;
  placeholder?: string;
  rules?: RulesProps[];
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "confirm", name: string): void;
}>();

const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const text = ref<string>(props.value ?? "");

const errors = computed(() => {
  return (
    props.rules?.reduce((acc, rule) => {
      if (!rule.validator(text.value || "")) {
        acc.push(rule.message);
      }
      return acc;
    }, [] as string[]) || []
  );
});

watch(showModal, () => {
  text.value = props.value ?? "";
});

const cancel = () => {
  showModal.value = false;
};

const confirm = () => {
  if (errors.value.length) return;
  emit("confirm", text.value.trim());
  showModal.value = false;
};

const inputRef = ref<InstanceType<typeof import("element-plus").ElInput>>();
watch(showModal, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      inputRef.value?.focus();
    }, 100);
  }
});
const { screen } = useScreen();
</script>

<template>
  <el-dialog
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    v-model="showModal"
    :title="title"
    append-to-body
    :width="screen.gt.sm ? '500px' : '100%'"
  >
    <el-form label-position="top" @submit.prevent>
      <div v-if="label?.length" class="text-grey pb-2">{{ label }}</div>
      <el-form-item>
        <el-input
          ref="inputRef"
          v-model="text"
          size="large"
          :placeholder="placeholder"
          @keyup.enter="confirm"
          autofocus
          :type="textarea ? 'textarea' : 'text'"
        >
        </el-input>
        <div>
          <div v-for="(error, i) in errors" :key="i">
            <span class="text-danger">{{ error }}</span>
          </div>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <div class="dialog-footer d-flex justify-content-end gap-2">
        <DRBtn :label="$t('cancel')" variant="outline" @click="cancel"></DRBtn>
        <DRBtn
          :label="$t('confirm')"
          @click="confirm"
          :disabled="!!errors.length"
        ></DRBtn>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped></style>
